import { type AppUser, type PageAbstract, PageOpenType } from '@lighthouse/core'
import { useAtomData } from '@lighthouse/shared'
import { find, reduce } from 'rambda'
import { useMemo } from 'react'

import { commonRoleIds } from '@/atoms/application/constants'
import { appRoleAtom } from '@/atoms/application/state'
import { useDataSourceDepartments, useDataSourceUserDetails } from '@/hooks/useDataSource'

import { usePageList } from './usePage'

export const useAppUser = function () {
    const developerRoleUserId = useAtomData(appRoleAtom)
    const users = useDataSourceUserDetails()
    const currentUser = useMemo(() => find(user => user.userId === developerRoleUserId, users), [developerRoleUserId, users])
    return useMemo(() => {
        if (commonRoleIds.includes(developerRoleUserId)) {
            return
        }
        return currentUser
    }, [currentUser, developerRoleUserId])
}

export const useUserPageList = function () {
    const developerRoleUserId = useAtomData(appRoleAtom)
    const pageList = usePageList()
    const currentUser = useAppUser()
    const departments = useDataSourceDepartments()

    const userPageList = useMemo(() => {
        const currentUserRoleId = currentUser?.roleId?.[0]
        const currentUserDepartmentIds = currentUser?.departmentIds ?? []
        const noNeedPageList = pageList.filter(item => item.open === PageOpenType.all)
        if (developerRoleUserId === 'visitor') {
            return noNeedPageList
        }
        if (developerRoleUserId === 'developer') {
            return pageList
        }
        return reduce<PageAbstract, PageAbstract[]>(
            (preVal, curVal) => {
                if (curVal.open === PageOpenType.part) {
                    const { roleIds = [], departmentIds = [] } = curVal
                    const isUserExitGroup = currentUserRoleId && roleIds.includes(currentUserRoleId)

                    const isUserExitDepartment = departmentIds.some(departmentId => currentUserDepartmentIds.includes(departmentId))

                    if (isUserExitGroup || isUserExitDepartment) {
                        return [...preVal, curVal]
                    }
                    return preVal
                }
                return [...preVal, curVal]
            },
            [],
            pageList
        )
    }, [currentUser?.roleId, currentUser?.departmentIds, pageList, developerRoleUserId])

    const allRolePageList = useMemo(() => {
        const currentUserRoleId = currentUser?.roleId?.[0]
        if (developerRoleUserId === 'visitor') {
            return pageList.map(page => ({ ...page, isAccess: page.open === PageOpenType.all }))
        }
        if (developerRoleUserId === 'developer') {
            return pageList.map(page => ({ ...page, isAccess: true }))
        }
        return pageList.map(page => {
            if (page.open === PageOpenType.part) {
                const { roleIds = [], departmentIds = [] } = page

                const isUserExitGroup = currentUserRoleId && roleIds.includes(currentUserRoleId)

                const isUserExitDepartment = departmentIds.some(id => currentUser?.departmentIds?.includes(id))

                return { ...page, isAccess: Boolean(isUserExitDepartment || isUserExitGroup) }
            }
            return { ...page, isAccess: true }
        })
    }, [currentUser?.roleId, currentUser?.departmentIds, developerRoleUserId, pageList])

    return useMemo(
        () => ({ visitPageList: userPageList, user: currentUser, developerRoleUserId, allRolePageList }),
        [allRolePageList, developerRoleUserId, currentUser, userPageList]
    )
}
